/**
 * ErrorListItemPath  component
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useMemo } from "react";
import { BEM } from "@adapptio/lib-components";
import { TModelPath, TParsePath } from "@adapptio/lib-blueprint";
import { Icon, ICON_SIZE } from "../Icon";

export interface IErrorListItemPathProps {
	path: TModelPath | TParsePath;
}

export const ErrorListItemPath = React.memo<IErrorListItemPathProps>(
	({ path }) => {
		const baseClass = BEM("edt-error-list", "item-path");
		const icon = useMemo(
			() => (
				<Icon
					className={baseClass.element("item-path-icon").toClassName()}
					name="mdi/menu-right"
					size={ICON_SIZE.SMALL}
					showLoader={false}
				/>
			),
			[ baseClass ]
		);

		return (
			<>
				{path.map((pathItem, index) => (
					<React.Fragment key={`path-el-${index}-${pathItem}`}>
						<span className={baseClass.element("item-path-el").toClassName()}>{pathItem}</span>
						{index < path.length - 1 && icon}
					</React.Fragment>
				))}
			</>
		);
	},
	(prevProps, nextProps) =>
		prevProps.path.length === nextProps.path.length &&
		prevProps.path.every((pathItem, index) => pathItem === nextProps.path[index])
);

ErrorListItemPath.displayName = "ErrorListItemPath";
